import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { getCustomLinkListAsync } from "src/api/customLink/custom-link";
import { CustomLinkListParams, CustomLinkModel } from "src/api/customLink/custom-link-types";
import { useApiOperation } from "src/api/hooks";
import { Modal, Order, PageMeta, Sort } from "src/api/public-types";
import { BaseButton, BaseInputWithSearch, BaseTable } from "src/components";
import SearchResetButton from "src/components/SearchResetButton";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { qsParse } from "src/utils";
import columns from "./columns/customLinkColumns";
import CustomLinkExposeManageModal from "./components/CustomLinkExposeManageModal";

// 검색어 옵션 (value 가 곧 API 요청 파라미터명. 전체는 search001)
const searchTypes = [
  {
    label: "전체",
    value: "",
  },
  {
    label: "링크 ID",
    value: "id",
  },
  {
    label: "링크명",
    value: "containsName",
  },
  {
    label: "링크 설명",
    value: "containsDescription",
  },
];

// 검색 옵션이 바뀌어도 URL 에 남는 이전 옵션의 검색 파라미터 (요청 전 제거 대상)
const searchParamKeys = ["id", "containsName", "containsDescription", "search001"] as const;

type QueryParams = {
  page?: number;
  size?: number;
  sort?: Sort;
  searchType?: string;
  keyword?: string;
  search001?: string;
  id?: string;
  containsName?: string;
  containsDescription?: string;
  isDisplayed?: string;
  containsTypes?: string;
};
/**
 * 외부 링크 연결 > 목록 화면
 */
const CustomLinkList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthority } = usePartnerAuthority();
  const queryParams: QueryParams = useMemo(() => {
    const _queryParams: QueryParams = qsParse(location.search);

    // page, size, sort 없이 최초 진입했을때 default 값 바인딩
    if (!_queryParams?.page) {
      _queryParams.page = 0;
    }
    if (!_queryParams?.size) {
      _queryParams.size = 20;
    }
    if (!_queryParams?.sort) {
      _queryParams.sort = {
        orders: [
          { property: "exposed", direction: "DESC" },
          { property: "displayOrderNumber", direction: "ASC", nullHandling: "NULLS_LAST" },
          { property: "id", direction: "ASC" },
        ],
      };
    }

    // qs.parse에 decoder때문에 검색값 입력 검색시 한번더 decoding
    if (_queryParams.keyword) {
      _queryParams.keyword = decodeURIComponent(_queryParams.keyword);
    }

    return _queryParams;
  }, [location]);

  const disabledSortHeaders = useMemo(() => columns.map((col) => col.accessor as string), []);
  const [params, setParams] = useState<QueryParams>({ ...queryParams });
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [customLinkList, setCustomLinkList] = useState<Array<CustomLinkModel>>([]); // table column에 맞게 가공한 목록
  const [exposeManageModal, setExposeManageModal] = useState<Modal>({ isOpen: false });
  const { executeAsync: getCustomLinkList } = useApiOperation(getCustomLinkListAsync);
  // 쿼리파람과 함께 navigate
  const navigateWithQueryParams = useCallback(
    (passParams?: QueryParams, type?: "search" | "pagination") => {
      let data;
      if (type) {
        type === "search" ? (data = { ...params }) : (data = { ...queryParams });
      }

      const newQueryParams = { ...data, ...(passParams || {}) };
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location.pathname, params, queryParams],
  );

  const handleCloseModal = useCallback(() => {
    setExposeManageModal({ isOpen: false });
  }, [setExposeManageModal]);

  const maxDisplayOrderNumber = useMemo(() => {
    const exposedItems = customLinkList.filter((item) => item.exposed);
    if (exposedItems.length === 0) return 0;
    return Math.max(...exposedItems.map((item) => item.displayOrderNumber || 0));
  }, [customLinkList]);

  const getCellClassName = useCallback(
    (row: any) => {
      const isLastExposedRow = row.original.exposed && row.original.displayOrderNumber === maxDisplayOrderNumber;
      return isLastExposedRow ? "last-exposed-row" : "";
    },
    [maxDisplayOrderNumber],
  );

  const fetchGetCustomLinkList = useCallback(async () => {
    // 이전 검색 옵션의 파라미터가 URL 에 남아 함께 전송되지 않도록 제거 후 현재 옵션만 세팅
    const { searchType, keyword, ...restParams } = queryParams;
    const searchQueryParams = { ...restParams } as Record<string, unknown>;
    searchParamKeys.forEach((key) => delete searchQueryParams[key]);
    searchQueryParams[searchType || "search001"] = keyword || "";

    const cleanPayload = Object.fromEntries(
      Object.entries(searchQueryParams).filter(([_, value]) => value !== null && value !== undefined && value !== ""),
    ) as CustomLinkListParams;

    // 외부 링크 연결 목록 조회
    const response = await getCustomLinkList(cleanPayload);
    const listData = response.data.data.content;
    setCustomLinkList(listData);
    setPageMeta(response.data.meta.pageMeta);
  }, [getCustomLinkList, queryParams]);
  useEffect(() => {
    setParams({ ...queryParams });
    fetchGetCustomLinkList();
  }, [queryParams, fetchGetCustomLinkList]);
  return (
    <div className="">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            {/* section 태그가 지정검색, 조건검색, 영역이고 반드시 필요함 */}
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <BaseInputWithSearch
                  type="text"
                  selectValue={params.searchType || ""}
                  inputValue={params.keyword || ""}
                  stateOptions={searchTypes}
                  setStateValue={(searchType: string) => {
                    setParams({ ...params, searchType });
                  }}
                  onChange={(keyword: string) => setParams({ ...params, keyword })}
                  onKeyUp={() => navigateWithQueryParams({ page: 0 }, "search")}
                  onSearchClick={() => navigateWithQueryParams({ page: 0 }, "search")}
                  onClearClick={() => navigateWithQueryParams({ page: 0, keyword: "" }, "search")}
                />
                <SearchResetButton />
              </div>
            </section>
          </div>
          {isAuthority("w") && (
            <div className="right-area flex-row flex-center-end gap10">
              <BaseButton
                title="노출 관리"
                className="color-white"
                onClick={() => {
                  setExposeManageModal({ isOpen: true });
                }}
              />
              <BaseButton
                title="+ 링크 등록"
                onClick={() => {
                  navigate(PagePath.customLink.form);
                }}
              />
            </div>
          )}
        </div>

        {customLinkList && (
          <BaseTable
            data={customLinkList}
            columns={columns}
            pageIndex={pageMeta?.pageRequest.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            goPage={(page: number) => {
              navigateWithQueryParams({ page }, "pagination");
            }}
            totalElements={Number(pageMeta?.totalElements) || 0}
            orders={queryParams?.sort?.orders}
            disabledSortHeaders={disabledSortHeaders}
            sizeOption={(number: number) => {
              setParams({ ...params, size: number });
              navigateWithQueryParams({ ...params, page: 0, size: number }, "search");
            }}
            currentSize={Number(params.size)}
            setOrders={(orders?: Array<Order>) => {
              if (orders) {
                navigateWithQueryParams({ ...params, sort: { orders } });
              }
            }}
            getCellClassName={getCellClassName}
          />
        )}
        {exposeManageModal.isOpen && (
          <CustomLinkExposeManageModal
            customLinkList={customLinkList}
            fetchGetCustomLinkList={fetchGetCustomLinkList}
            handleCloseModal={handleCloseModal}
          />
        )}
      </div>
    </div>
  );
};

export default CustomLinkList;
